import { computed, type ComputedRef, type Ref, ref, toRef, watch } from 'vue'
import { useRoute } from 'vue-router'
import type { EngagementRingProps } from '@/views/product/engagement-ring/er.ts'
import type {
    BlockFragment,
    CatalogueEngagementRingFragment,
    CatalogueEngagementRingSignatureStoneInput,
    DeliveryTimeframeFragment,
    EngagementRingAndSignatureStoneFragment,
    EngagementRingPageQuery,
    EngagementRingPageQueryVariables,
} from '@/graphql/api'
import { useTranslator } from '@/i18n'
import { useCountryPlugin } from '@/plugins/country.ts'
import {
    createSignatureStoneFinderPredicate,
    getEngagementRingFilterInputFromProps,
    isSameRing,
    isSameStone,
    signatureStoneToInput,
} from '@/domain/product/ring/engagement'
import { useDeliveryDate } from '@/views/product/composables/useDeliveryDate'

import { useCurrencyInput } from '@/plugins/currency-input.ts'
import { useEngagementRingPageQuery } from '@/views/product/engagement-ring/EngagementRingPage.generated'
import type { CombinedError, UseQueryResponse } from '@urql/vue'
import type { RingBuilder } from '@/plugins/ring-builder.ts'

export const getSignatureStoneLabel = (
    stone: EngagementRingAndSignatureStoneFragment['signatureStone']
): string =>
    stone.crystal.systemCode === 'DIAMOND'
        ? stone.crystal.translation.name
        : `${stone.color.translation.name} ${stone.crystal.translation.name}`

export interface EngagementRingData {
    ring: Ref<CatalogueEngagementRingFragment | null | undefined>
    fetching: Ref<boolean>
    stale: Ref<boolean>
    error: Ref<CombinedError | undefined>
    responsePage: UseQueryResponse<EngagementRingPageQuery>
    cmsBlocks: Ref<({ __typename: 'Block' } & BlockFragment)[] | undefined>
    executePageQuery(): UseQueryResponse<EngagementRingPageQuery>
    is3rdStep: ComputedRef<boolean>
    isValidCreation: Readonly<Ref<boolean>>
    deliveryDate: Readonly<Ref<DeliveryTimeframeFragment | undefined>>
    signatureStone: Ref<CatalogueEngagementRingSignatureStoneInput | undefined>
    signatureStoneLabel: ComputedRef<string | undefined>
    signatureStoneOutput: ComputedRef<
        | ({
              __typename: 'CatalogueEngagementRingAndSignatureStone'
          } & EngagementRingAndSignatureStoneFragment)
        | undefined
    >
}

export async function useEngagementRingData(
    props: EngagementRingProps,
    hasEngraving: Readonly<Ref<boolean>>,
    ringBuilder: RingBuilder
): Promise<EngagementRingData> {
    const {
        addRingAndStone,
        ring: rbRing,
        stone: rbStone,
        mountFragment,
        signatureStone: rbSignatureStone,
        isValid,
    } = ringBuilder

    const is3rdStep = computed(
        () =>
            rbStone.value !== null &&
            rbRing.value !== null &&
            props.slug === rbRing.value.slug &&
            props.metal === rbRing.value.primaryMetal.systemCode &&
            props.suffixSlug === rbRing.value.suffixSlug &&
            props.shape === rbRing.value.centreStoneConstraints.shape.systemCode
    )

    const currency = useCurrencyInput()
    const { countryCode } = useCountryPlugin()
    const { locale } = useTranslator()
    const page = useRoute().path

    const variables = computed((): EngagementRingPageQueryVariables => {
        return {
            locale: locale.value,
            currency: currency.value,
            country: countryCode.value!,
            page,
            pageFallback: 'engagement-rings',
            engagementRingFilter: getEngagementRingFilterInputFromProps(props),
        }
    })

    const responsePage = useEngagementRingPageQuery({
        variables,
    })

    const catalogueDeliveryDate = useDeliveryDate(responsePage, (data) =>
        hasEngraving.value
            ? data.catalogueEngagementRing?.hasEngravingDeliveryDate
            : data.catalogueEngagementRing?.noEngravingDeliveryDate
    )

    const {
        fetching,
        stale,
        error,
        data,
        executeQuery: executePageQuery,
    } = responsePage

    // In SSR, to avoid mismatches, we run this very early on in AppContentTop.vue
    if (
        props.nonSku &&
        (!isSameRing(rbRing.value, props) ||
            !isSameStone(rbStone.value, props.nonSku))
    ) {
        await addRingAndStone(
            getEngagementRingFilterInputFromProps(props),
            props.nonSku
        )
    }

    await responsePage

    const cmsBlocks = toRef(() => data.value?.pageProductDetails?.blocks)

    const ring = toRef(() => data.value?.catalogueEngagementRing)

    const signatureStone = ref<
        CatalogueEngagementRingSignatureStoneInput | undefined
    >()

    watch(
        ring,
        (newRing) => {
            if (!newRing) {
                return
            }

            const getters: (() =>
                | EngagementRingAndSignatureStoneFragment
                | undefined
                | null)[] = []

            getters.push(() => {
                const rbStone = rbSignatureStone.value

                if (!rbStone) {
                    return
                }

                const rbStonePredicate =
                    createSignatureStoneFinderPredicate(rbStone)
                return newRing.signatureStones.find((s) =>
                    rbStonePredicate(s.signatureStone)
                )
            })

            getters.push(() => {
                const mountStone = mountFragment.value?.signatureStone

                if (!mountStone) {
                    return
                }

                const mountStonePredicate = createSignatureStoneFinderPredicate(
                    signatureStoneToInput(mountStone)
                )

                return newRing.signatureStones.find((s) =>
                    mountStonePredicate(s.signatureStone)
                )
            })

            getters.push(() => {
                const ringStone = newRing.signatureStone
                if (!ringStone || !ringStone.colors[0]) {
                    return
                }
                const ringStonePredicate = createSignatureStoneFinderPredicate({
                    color: ringStone.colors[0].systemCode,
                    crystal: ringStone.crystal.systemCode,
                    width: ringStone.width,
                })
                return newRing.signatureStones.find((s) =>
                    ringStonePredicate(s.signatureStone)
                )
            })

            getters.push(() => {
                return newRing.signatureStones.find(
                    (stone) => stone.signatureStone.isDefault
                )
            })

            getters.push(() => {
                return newRing.signatureStones[0]
            })

            let output = undefined

            for (let i = 0; i < getters.length && output === undefined; i++) {
                output = getters[i]!()
            }

            if (output) {
                signatureStone.value = signatureStoneToInput(
                    output.signatureStone
                )
            }
        },
        { immediate: true }
    )

    const signatureStoneOutput = computed(() => {
        const stones = ring.value?.signatureStones

        if (!stones || !signatureStone.value) {
            return
        }

        const predicate = createSignatureStoneFinderPredicate(
            signatureStone.value
        )

        return stones.find((value) => predicate(value.signatureStone))
    })

    const signatureStoneLabel = computed(() => {
        const sigStoneOutput = signatureStoneOutput.value

        if (!sigStoneOutput) {
            return undefined
        }

        return getSignatureStoneLabel(sigStoneOutput.signatureStone)
    })

    return {
        ring,
        fetching,
        stale,
        error,
        responsePage,
        cmsBlocks,
        executePageQuery,
        is3rdStep,
        isValidCreation: isValid,
        deliveryDate: catalogueDeliveryDate,
        signatureStone,
        signatureStoneLabel,
        signatureStoneOutput,
    }
}
